import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Reveal } from "@/lib/motion";
import { Chapter } from "./Chapter";
import { Mail, Phone, Download, Loader2, Send } from "lucide-react";
import { FaLinkedinIn, FaXTwitter, FaWhatsapp } from "react-icons/fa6";
import { CONTACT } from "@/constants/testIds";
import { PROFILE } from "@/data/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Field = ({ id, label, type = "text", value, onChange, testid, textarea, placeholder }) => (
  <div>
    <label htmlFor={id} className="block font-mono-plex text-[12.5px] uppercase tracking-[0.16em] text-[#79d6bd] mb-2">
      {label}
    </label>
    {textarea ? (
      <textarea
        id={id}
        data-testid={testid}
        value={value}
        onChange={onChange}
        rows={5}
        placeholder={placeholder}
        className="w-full bg-[#eef0e9] border border-[#d2d5ce] rounded-xl px-4 py-3.5 text-[#16324f] text-[15px] placeholder:text-[#8b96a1] outline-none focus:border-[#b5423a] focus:ring-2 focus:ring-[#b5423a]/15 resize-none transition-[border-color,box-shadow] duration-300"
      />
    ) : (
      <input
        id={id}
        type={type}
        data-testid={testid}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        className="w-full bg-[#eef0e9] border border-[#d2d5ce] rounded-xl px-4 py-3.5 text-[#16324f] text-[15px] placeholder:text-[#8b96a1] outline-none focus:border-[#b5423a] focus:ring-2 focus:ring-[#b5423a]/15 transition-[border-color,box-shadow] duration-300"
      />
    )}
  </div>
);

const InfoCard = ({ icon: Icon, label, value, href, value2, href2 }) => (
  <div className="flex items-center gap-4 bg-[#16324f] rounded-2xl p-5">
    <span className="shrink-0 w-11 h-11 rounded-full bg-[#f5f6f2] text-[#16324f] flex items-center justify-center">
      <Icon size={18} strokeWidth={1.8} />
    </span>
    <div>
      <p className="font-mono-plex text-[12.5px] uppercase tracking-[0.16em] text-[#79d6bd]">{label}</p>
      {href ? (
        <a href={href} className="font-serif-display text-lg text-[#f5f6f2] w-fit block">{value}</a>
      ) : (
        <p className="font-serif-display text-lg text-[#f5f6f2]">{value}</p>
      )}
      {value2 && (
        href2 ? (
          <a href={href2} className="font-serif-display text-lg text-[#f5f6f2] w-fit block">{value2}</a>
        ) : (
          <p className="font-serif-display text-lg text-[#f5f6f2]">{value2}</p>
        )
      )}
    </div>
  </div>
);

const SocialBtn = ({ icon: Icon, label, href, bg, external }) => (
  <a
    href={href}
    {...(external ? { target: "_blank", rel: "noopener noreferrer" } : {})}
    aria-label={label}
    title={label}
    style={{ background: bg }}
    className="social-btn w-9 h-9 rounded-full text-white flex items-center justify-center"
  >
    <Icon size={18} />
  </a>
);

const ConnectCard = () => (
  <div className="bg-[#16324f] rounded-2xl p-6">
    <p className="font-mono-plex text-[13px] font-semibold uppercase tracking-[0.16em] text-[#79d6bd] mb-4">Connect with me</p>
    <div className="flex flex-wrap gap-3">
      <SocialBtn icon={FaLinkedinIn} label="LinkedIn" href="#hero" bg="#0A66C2" />
      <SocialBtn icon={FaXTwitter} label="X (Twitter)" href="#hero" bg="#000000" />
      <SocialBtn icon={FaWhatsapp} label="WhatsApp · +254 706 037437" href="https://wa.me/254706037437" bg="#25D366" external />
    </div>
  </div>
);

export const Contact = () => {
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "", website: "" });
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill in your name, email and message.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Message sent — thank you! Catherine will be in touch.");
      setForm({ name: "", email: "", subject: "", message: "", website: "" });
    } catch (err) {
      toast.error("Something went wrong. Please email directly instead.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" className="relative z-10 pt-24 md:pt-32 lg:pt-40 pb-16">
      <div className="max-w-screen-2xl mx-auto px-6 md:px-12 lg:px-20">
        <Chapter num="09" title="Get in touch" kicker="Reach out" />
        <Reveal>
          <p className="font-serif-display font-light text-[#16324f] text-2xl md:text-4xl leading-[1.12] tracking-[-0.01em] max-w-3xl">
            Open to public health, epidemiology & health-data roles <span className="text-[#b5423a]">worldwide.</span>
          </p>
        </Reveal>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 mt-16 md:mt-24 items-stretch">
          {/* Left — contact info cards */}
          <div className="lg:col-span-5 order-2 lg:order-1 flex flex-col gap-5 h-full">
            <InfoCard icon={Mail} label="Email" value={PROFILE.email} href={`mailto:${PROFILE.email}`} />
            <InfoCard icon={Phone} label="Phone" value={PROFILE.phone} href={`tel:${PROFILE.phone.replace(/\s/g, "")}`} value2="+255 748 443 658" href2="tel:+255748443658" />
            <ConnectCard />
            <div className="flex-1">
              <div className="h-full flex flex-col justify-between bg-[#16324f] rounded-2xl p-6 md:pb-9">
                <div>
                  <p className="font-mono-plex text-[14px] font-semibold uppercase tracking-[0.16em] text-[#79d6bd]">Open to opportunities</p>
                  <p className="mt-3 text-[16px] md:text-[17px] text-[#eef1f3] leading-relaxed">
                    Graduated 2026 and looking for a first full-time role in epidemiology, biostatistics or public-health — anywhere in the world.
                  </p>
                </div>
                <div className="mt-5 flex flex-wrap gap-3">
                  <a
                    data-testid={CONTACT.downloadCv}
                    href="/Catherine-Magawa-CV.pdf"
                    download
                    className="inline-flex items-center gap-2 self-start border border-[#f5f6f2]/40 text-[#f5f6f2] font-mono-plex text-[11.5px] uppercase tracking-[0.12em] px-5 py-2.5 rounded-full hover:bg-[#b5423a] hover:border-[#b5423a] transition-colors duration-400"
                  >
                    <Download size={13} /> Download CV
                  </a>
                  <a
                    data-testid={CONTACT.downloadRefs}
                    href="/Catherine-Magawa-References.pdf"
                    download
                    className="inline-flex items-center gap-2 self-start border border-[#f5f6f2]/40 text-[#f5f6f2] font-mono-plex text-[11.5px] uppercase tracking-[0.12em] px-5 py-2.5 rounded-full hover:bg-[#b5423a] hover:border-[#b5423a] transition-colors duration-400"
                  >
                    <Download size={13} /> Download Refs
                  </a>
                </div>
              </div>
            </div>
          </div>

          {/* Right — form card */}
          <div className="lg:col-span-7 order-1 lg:order-2">
            <div className="h-full">
              <div className="h-full bg-[#16324f] rounded-3xl p-6 md:p-9">
                <form data-testid={CONTACT.form} onSubmit={submit} className="space-y-6">
                  <Field id="c-name" label="Name" value={form.name} onChange={set("name")} testid={CONTACT.name} placeholder="Your name" />
                  <Field id="c-email" label="Email" type="email" value={form.email} onChange={set("email")} testid={CONTACT.email} placeholder="your.email@example.com" />
                  <Field id="c-subject" label="Subject (optional)" value={form.subject} onChange={set("subject")} testid={CONTACT.subject} placeholder="What's this about?" />
                  <Field id="c-message" label="Message" value={form.message} onChange={set("message")} testid={CONTACT.message} placeholder="Your message..." textarea />
                  <div aria-hidden="true" style={{ position: "absolute", left: "-9999px", width: "1px", height: "1px", overflow: "hidden" }}>
                    <label htmlFor="c-website">Website (leave blank)</label>
                    <input
                      id="c-website"
                      type="text"
                      tabIndex={-1}
                      autoComplete="off"
                      value={form.website}
                      onChange={set("website")}
                    />
                  </div>
                  <button
                    data-testid={CONTACT.submit}
                    type="submit"
                    disabled={loading}
                    className="group w-full flex items-center justify-center gap-2.5 bg-[#b5423a] text-[#f5f6f2] font-mono-plex text-[12.5px] uppercase tracking-[0.14em] px-8 py-4 rounded-xl hover:bg-[#9c3730] transition-colors duration-400 disabled:opacity-60"
                  >
                    {loading ? <Loader2 size={16} className="animate-spin" /> : <Send size={15} className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform duration-300" />}
                    {loading ? "Sending…" : "Send message"}
                  </button>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
